// src/views/User/HomeUser.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

import { Line, Bar, Pie } from 'react-chartjs-2';
import { faHeartbeat, faTemperatureHigh, faTint, faChair } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

export default function HomeUser() {
  const { user } = useAuth();
  const [datos, setDatos] = useState([]);
  const [posturas, setPosturas] = useState([]);

  useEffect(() => {
    if (!user) return;

    const fetchDatos = async () => {
      try {
        const res = await axios.get(`/api/users/signos/usuario/${user.id}`);
        const reversed = res.data.reverse();
        setDatos(reversed);
        setPosturas(reversed);
      } catch (err) {
        console.error('Error al obtener datos:', err);
      }
    };

    fetchDatos();
    const interval = setInterval(fetchDatos, 5000);
    return () => clearInterval(interval);
  }, [user]);

  const labels = datos.map(d => new Date(d.timestamp).toLocaleTimeString('es-MX'));

  // Gráficas de signos vitales
  const ritmoCardiacoData = {
    labels,
    datasets: [{
      label: 'Ritmo (ppm)',
      data: datos.map(d => d.ritmo_cardiaco),
      borderColor: 'rgb(255, 99, 132)',
      backgroundColor: 'rgba(255, 99, 132, 0.3)',
      tension: 0.3,
      fill: true
    }]
  };

  const temperaturaData = {
    labels,
    datasets: [{
      label: 'Temp (°C)',
      data: datos.map(d => d.temp_corporal),
      backgroundColor: 'rgba(54, 162, 235, 0.6)',
      borderColor: 'rgb(54, 162, 235)',
      borderWidth: 1
    }]
  };

  const spo2Data = {
    labels,
    datasets: [{
      label: 'SpO2 (%)',
      data: datos.map(d => d.spo2),
      backgroundColor: 'rgba(75, 192, 192, 0.6)',
      borderColor: 'rgb(75, 192, 192)',
      borderWidth: 1
    }]
  };

  const opcionesGraficas = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      y: { beginAtZero: true }
    }
  };

  // Datos para gráfica de postura
  const ultimos = posturas.slice(-50);
  const conteo = ultimos.reduce((acc, curr) => {
    if (curr.postura === 'mala') acc.mala++;
    else acc.buena++;
    return acc;
  }, { buena: 0, mala: 0 });

  const posturaBarData = {
    labels: ['Buena Postura', 'Mala Postura'],
    datasets: [
      {
        label: 'Cantidad',
        data: [conteo.buena, conteo.mala],
        backgroundColor: ['#22c55e', '#b91c1c'], // verde y rojo oscuro
        borderRadius: 6,
      },
    ],
  };


  return (
    <div className="max-w-[1200px] mx-auto p-8 backdrop-blur-md bg-white/30 shadow-md rounded-2xl">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Hola, {user?.nombre}!</h1>

       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Ritmo Cardiaco */}
        <div className="bg-white/80 p-4 rounded-xl shadow-md">
          <div className="flex items-center gap-2 mb-2 text-red-600 font-semibold text-md">
            <FontAwesomeIcon icon={faHeartbeat} />
            <span>Ritmo Cardíaco</span>
          </div>
          <Line data={ritmoCardiacoData} options={opcionesGraficas} />
        </div>

        {/* Temperatura */}
        <div className="bg-white/80 p-4 rounded-xl shadow-md">
          <div className="flex items-center gap-2 mb-2 text-blue-600 font-semibold text-md">
            <FontAwesomeIcon icon={faTemperatureHigh} />
            <span>Temperatura</span>
          </div>
          <Bar data={temperaturaData} options={opcionesGraficas} />
        </div>

        {/* SpO2 */}
        <div className="bg-white/80 p-4 rounded-xl shadow-md">
          <div className="flex items-center gap-2 mb-2 text-teal-600 font-semibold text-md">
            <FontAwesomeIcon icon={faTint} />
            <span>Oxígeno en Sangre</span>
          </div>
          <Bar data={spo2Data} options={opcionesGraficas} />
        </div>

        {/* Postura */}
        <div className="bg-white/80 p-4 rounded-xl shadow-md">
          <div className="flex items-center gap-2 mb-2 text-purple-600 font-semibold text-md">
            <FontAwesomeIcon icon={faChair} />
            <span>Postura</span>
          </div>
            <Bar data={posturaBarData} options={opcionesGraficas} />
        </div>
      </div>
    </div>
  );
}
